import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Radio } from "lucide-react";
import { PageHeader } from "@/components/DashboardShell";
import { StatusPill, Stat } from "@/components/brand";
import InstallPrompt from "@/components/InstallPrompt";
import PushToggle from "@/components/PushToggle";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function DriverOverview() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [sharing, setSharing] = useState(user?.live_location?.sharing || false);

  const load = useCallback(async () => {
    const [s, d] = await Promise.all([api.get("/stats/driver"), api.get("/deliveries")]);
    setStats(s.data);
    setDeliveries(d.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleShare = async () => {
    const next = !sharing;
    setSharing(next);
    try {
      const coords = { lat: 43.6532 + Math.random() * 0.05, lng: -79.3832 + Math.random() * 0.05 };
      await api.patch("/drivers/me/location", next ? { ...coords, sharing: true } : { sharing: false });
    } catch { setSharing(!next); }
  };

  const active = deliveries.filter((d) => ["assigned", "picked_up", "en_route"].includes(d.status));

  return (
    <div>
      <PageHeader
        index="01" eyebrow="Driver Overview" tag={user?.region ? `Region · ${user.region}` : ""}
        title={`Today, ${user?.name?.split(" ")[0]}.`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <PushToggle />
            <button
              onClick={toggleShare}
              data-testid="live-location-toggle"
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm border transition-colors ${sharing ? "bg-orange-500 border-orange-500 text-white" : "border-gray-300 text-gray-600 hover:border-[#0a0a0a]"}`}
            >
              <Radio className="h-4 w-4" /> {sharing ? "Sharing location" : "Share location"}
            </button>
          </div>
        }
      />

      <InstallPrompt />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 border border-gray-200 p-6 mb-10">
        <Stat label="Active" value={stats?.active ?? "—"} />
        <Stat label="Delivered" value={stats?.delivered ?? "—"} />
        <Stat label="Total jobs" value={stats?.total ?? "—"} />
        <Stat label="Earnings" value={stats ? `$${stats.earnings.toFixed(2)}` : "—"} />
      </div>

      <div className="flex items-center gap-4 mb-4">
        <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">Assigned deliveries</span>
        <span className="h-px flex-1 bg-gray-200" />
        <Link to="/driver/deliveries" className="font-mono text-[11px] tracking-[0.14em] uppercase text-orange-600 hover:underline">View all</Link>
      </div>

      <div className="border border-gray-200 divide-y divide-gray-200" data-testid="driver-active-list">
        {active.length === 0 && <div className="px-5 py-10 text-center text-sm text-gray-400">No active deliveries right now.</div>}
        {active.map((d) => (
          <Link key={d.id} to={`/driver/deliveries/${d.id}`} className="flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors" data-testid={`delivery-row-${d.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-3">
                <span className="font-mono text-[11px] text-gray-400">{d.id.slice(0, 8).toUpperCase()}</span>
                <StatusPill status={d.status} />
              </div>
              <div className="mt-1.5 text-sm text-[#0a0a0a] truncate">{d.pickup.address} → {d.dropoff.address}</div>
              <div className="text-xs text-gray-500">{d.merchant_name} · {d.package_description}</div>
            </div>
            <span className="font-mono text-[11px] tracking-[0.14em] uppercase text-gray-400 shrink-0 ml-4">Open →</span>
          </Link>
        ))}
      </div>
    </div>
  );
}
